<template>
  <Dialog :open="open" title="Temporary Disarm" :maxWidth="420" showClose @close="$emit('close')">
    <div class="td-create">
      <div class="td-create__sub">{{ partitionName || 'Partition' }}</div>

      <div class="note">
        This partition will be disarmed for the selected time, then automatically re-armed.
      </div>

      <div class="label">Disarm duration</div>
      <div class="durations">
        <button
          v-for="d in durations"
          :key="d.key"
          class="duration"
          :class="{ 'duration--active': selectedKey === d.key }"
          @click="selectedKey = d.key">
          {{ d.label }}
        </button>
      </div>

      <div class="preview">
        <ion-icon name="time-outline" />
        <span>Re-arms in <strong>{{ rearmPreview }}</strong></span>
      </div>
    </div>

    <template #actions>
      <button class="dlg-btn dlg-btn--ghost" @click="$emit('close')">Cancel</button>
      <button class="dlg-btn dlg-btn--confirm" :disabled="submitting" @click="onConfirm">
        <ion-spinner v-if="submitting" name="crescent" />
        <span v-else>Disarm</span>
      </button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from '@/components/Dialog'
import { TEMP_DISARM_DURATIONS, resolveTempDisarmDuration } from '@/models/tempDisarm'

// End-user temporary-disarm creation, shown as a centered dialog. Driven app-wide
// from App.vue via the tempDisarmCreateOpen / tempDisarmCreateCtx store state.
export default {
  name: 'temp-disarm-create-dialog',
  components: { Dialog },
  props: {
    open: { type: Boolean, default: false },
    ctx: { type: Object, default: null }, // { partitionId, siteId, partitionName }
    store: { type: Object, default: null },
  },
  data: function () {
    return {
      durations: TEMP_DISARM_DURATIONS,
      selectedKey: '30min',
      submitting: false,
    }
  },
  computed: {
    bpStore: function () {
      return this.store || this.$store
    },
    partitionId: function () {
      return (this.ctx && this.ctx.partitionId) != null ? this.ctx.partitionId : null
    },
    siteId: function () {
      return (this.ctx && this.ctx.siteId) || null
    },
    partitionName: function () {
      return (this.ctx && this.ctx.partitionName) || ''
    },
    rearmPreview: function () {
      const now = this.bpStore.getters ? this.bpStore.getters.tempDisarmNow : Date.now()
      const d = resolveTempDisarmDuration(this.selectedKey, now)
      // Relative duration, never an absolute clock (see formatRemaining).
      return d.label
    },
  },
  watch: {
    open: function (val) {
      if (!val) return
      this.selectedKey = '30min'
      this.submitting = false
    },
  },
  methods: {
    onConfirm: async function () {
      if (this.submitting) return
      this.submitting = true
      try {
        await this.bpStore.dispatch('tempDisarmPartition', {
          partitionId: this.partitionId,
          siteId: this.siteId,
          durationKey: this.selectedKey,
        })
        this.$emit('close')
      } catch (err) {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.td-create__sub {
  font-size: 14px;
  opacity: 0.6;
  margin: -4px 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 18px;
  color: var(--ion-color-tertiary);
  background: rgba(30, 166, 117, 0.12);
}
.label {
  margin: 18px 0 10px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.durations {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.duration {
  padding: 12px 6px;
  border-radius: 10px;
  border: 1.5px solid var(--ion-color-dark-shade);
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--ion-text-color);
  cursor: pointer;
  transition: all 0.15s ease;
}
.duration--active {
  border-color: var(--ion-color-tertiary);
  background: rgba(30, 166, 117, 0.16);
  color: var(--ion-color-tertiary);
}
.preview {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  font-size: 14px;
  opacity: 0.85;
}
.preview ion-icon {
  color: var(--ion-color-tertiary);
  font-size: 18px;
}
.dlg-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 0;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s ease;
}
.dlg-btn[disabled] {
  opacity: 0.5;
  cursor: default;
}
.dlg-btn--ghost {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.22);
  color: var(--ion-text-color);
}
.dlg-btn--confirm {
  background: var(--ion-color-tertiary);
  color: #ffffff;
}
.dlg-btn--confirm ion-spinner {
  width: 18px;
  height: 18px;
}
</style>
